import { useState } from "react";
import { css, cx } from "styled-system/css";
import { Eye, EyeOff, GripVertical, X } from "lucide-react";
import { cf } from "@/components/brand/sfturnos-styles";
import { useConfirm } from "@/hooks/useConfirm";
import {
  applyOrder,
  createCustomQuestion,
  NUEVA_CONSULTA_DEFAULT_FIELDS,
  type CustomQuestionType,
  type FormSectionConfig,
  type FormsConfig,
} from "@/pages/dashboard/odontogram/formsConfig";
import { QUESTIONNAIRE_ITEMS_DEF } from "@/pages/dashboard/odontogram/medicalQuestionnaire";

type Props = {
  value: FormsConfig;
  onChange: (next: FormsConfig) => void;
};

export function SettingsCustomFormsBlock({ value, onChange }: Props) {
  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "5" })}>
      <SettingsFormSection
        title="Antecedentes médicos"
        description="Preguntas del cuestionario de antecedentes (ficha médico-odontológica). Podés ocultar las que no uses, agregar nuevas y reordenarlas."
        defaultItems={QUESTIONNAIRE_ITEMS_DEF.map((d) => ({ id: d.key, label: d.label }))}
        defaultsHideable
        value={value.antecedentes ?? {}}
        onChange={(next) => onChange({ ...value, antecedentes: next })}
      />
      <SettingsFormSection
        title="Nueva consulta"
        description="Campos del formulario de consulta. Motivo, notas y tratamientos son fijos; podés agregar preguntas adicionales."
        defaultItems={NUEVA_CONSULTA_DEFAULT_FIELDS}
        defaultsHideable={false}
        value={value.nuevaConsulta ?? {}}
        onChange={(next) => onChange({ ...value, nuevaConsulta: next })}
      />
    </div>
  );
}

type DefaultItemDef = { id: string; label: string };

type Row =
  | { kind: "default"; id: string; label: string; hidden: boolean }
  | { kind: "custom"; id: string; label: string; type: CustomQuestionType };

function SettingsFormSection({
  title,
  description,
  defaultItems,
  defaultsHideable,
  value,
  onChange,
}: {
  title: string;
  description: string;
  defaultItems: DefaultItemDef[];
  /** Antecedentes: cada default se puede ocultar/mostrar y entra en el reorder general.
   *  Nueva consulta: los defaults son fijos (no se ocultan, no se arrastran). */
  defaultsHideable: boolean;
  value: FormSectionConfig;
  onChange: (next: FormSectionConfig) => void;
}) {
  const confirm = useConfirm();
  const [draft, setDraft] = useState("");
  const [draftType, setDraftType] = useState<CustomQuestionType>("yesno");
  const [dragId, setDragId] = useState<string | null>(null);

  const hiddenSet = new Set(value.hiddenDefaultIds ?? []);
  const customItems = value.customItems ?? [];

  const defaultRows: Row[] = defaultItems.map((d) => ({
    kind: "default",
    id: d.id,
    label: d.label,
    hidden: hiddenSet.has(d.id),
  }));
  const customRows: Row[] = customItems.map((c) => ({ kind: "custom", id: c.id, label: c.label, type: c.type }));

  const orderableRows = applyOrder(
    defaultsHideable ? [...defaultRows, ...customRows] : customRows,
    (r) => r.id,
    value.order,
  );

  function toggleHidden(id: string) {
    const next = hiddenSet.has(id) ? [...hiddenSet].filter((x) => x !== id) : [...hiddenSet, id];
    onChange({ ...value, hiddenDefaultIds: next });
  }

  async function removeCustom(row: Row) {
    if (row.kind !== "custom") return;
    const ok = await confirm({
      title: "Eliminar pregunta",
      message: `¿Eliminar "${row.label}"? Las respuestas ya guardadas en fichas existentes no se borran, pero la pregunta deja de mostrarse a partir de ahora.`,
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;
    onChange({
      ...value,
      customItems: customItems.filter((c) => c.id !== row.id),
      order: (value.order ?? []).filter((id) => id !== row.id),
    });
  }

  function addCustom() {
    const label = draft.trim();
    if (!label) return;
    const q = createCustomQuestion(label, draftType);
    onChange({ ...value, customItems: [...customItems, q] });
    setDraft("");
    setDraftType("yesno");
  }

  function handleDrop(targetId: string) {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const ids = orderableRows.map((r) => r.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    setDragId(null);
    if (from === -1 || to === -1) return;
    const next = ids.slice();
    next.splice(from, 1);
    next.splice(to, 0, dragId);
    onChange({ ...value, order: next });
  }

  return (
    <div className={cx(cf.dashSurface, css({ p: "5" }))}>
      <p className={css({ fontWeight: "semibold", color: "#0f172a", mb: "1" })}>{title}</p>
      <p className={css({ fontSize: "xs", color: "#64748b", mb: "4", lineHeight: "1.5" })}>{description}</p>

      {!defaultsHideable && (
        <div className={css({ display: "flex", flexDir: "column", gap: "1", mb: "3" })}>
          {defaultItems.map((d) => (
            <div
              key={d.id}
              className={css({
                display: "flex",
                alignItems: "center",
                gap: "2",
                px: "3",
                py: "2",
                rounded: "md",
                bg: "#f8fafc",
                fontSize: "sm",
                color: "#334155",
              })}
            >
              <span
                className={css({
                  fontSize: "2xs",
                  fontWeight: "semibold",
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "wide",
                  flexShrink: 0,
                })}
              >
                Fijo
              </span>
              {d.label}
            </div>
          ))}
        </div>
      )}

      {orderableRows.length > 0 && (
        <div className={css({ display: "flex", flexDir: "column", gap: "1", mb: "4" })}>
          {orderableRows.map((row) => (
            <div
              key={row.id}
              draggable
              onDragStart={() => setDragId(row.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(row.id)}
              onDragEnd={() => setDragId(null)}
              className={cx(
                css({
                  display: "flex",
                  alignItems: "center",
                  gap: "2",
                  px: "3",
                  py: "2",
                  rounded: "md",
                  borderWidth: "1px",
                  borderColor: "#e2e8f0",
                  bg: "white",
                  fontSize: "sm",
                  color: "#334155",
                  cursor: "grab",
                  transition: "opacity 120ms ease, background 120ms ease",
                  _hover: { bg: "#f8fafc" },
                }),
                row.kind === "default" && row.hidden ? css({ opacity: 0.5 }) : undefined,
                dragId === row.id ? css({ opacity: 0.35 }) : undefined,
              )}
            >
              <GripVertical size={14} className={css({ color: "#cbd5e1", flexShrink: 0 })} aria-hidden />
              <span className={css({ flex: 1, minW: 0, truncate: true })}>{row.label}</span>
              {row.kind === "default" ? (
                <button
                  type="button"
                  onClick={() => toggleHidden(row.id)}
                  aria-label={row.hidden ? `Mostrar "${row.label}"` : `Ocultar "${row.label}"`}
                  className={css({
                    display: "inline-flex",
                    flexShrink: 0,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#94a3b8",
                    p: "1",
                    rounded: "sm",
                    transition: "transform 120ms cubic-bezier(0.23, 1, 0.32, 1), color 120ms ease",
                    _hover: { color: "#64748b" },
                    _active: { transform: "scale(0.9)" },
                  })}
                >
                  {row.hidden ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              ) : (
                <>
                  <span className={css({ fontSize: "2xs", color: "#94a3b8", flexShrink: 0 })}>
                    {row.type === "yesno" ? "Sí/No" : "Texto"}
                  </span>
                  <button
                    type="button"
                    onClick={() => void removeCustom(row)}
                    aria-label={`Eliminar "${row.label}"`}
                    className={css({
                      display: "inline-flex",
                      flexShrink: 0,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#94a3b8",
                      p: "1",
                      rounded: "sm",
                      transition: "transform 120ms cubic-bezier(0.23, 1, 0.32, 1), color 120ms ease",
                      _hover: { color: "#dc2626" },
                      _active: { transform: "scale(0.9)" },
                    })}
                  >
                    <X size={15} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
        <input
          className={cx(cf.input, css({ flex: 1, minW: "180px" }))}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder="Nueva pregunta…"
          maxLength={200}
        />
        <select
          value={draftType}
          onChange={(e) => setDraftType(e.target.value as CustomQuestionType)}
          className={css({
            fontSize: "sm",
            color: "#0f172a",
            bg: "white",
            borderWidth: "1px",
            borderColor: "#cbd5e1",
            rounded: "md",
            px: "3",
            outline: "none",
            cursor: "pointer",
          })}
        >
          <option value="yesno">Sí / No</option>
          <option value="text">Texto libre</option>
        </select>
        <button
          type="button"
          onClick={addCustom}
          disabled={!draft.trim()}
          className={css({
            px: "4",
            py: "2",
            rounded: "md",
            fontSize: "sm",
            fontWeight: "medium",
            borderWidth: "1px",
            borderColor: "#cbd5e1",
            bg: "#fff",
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "transform 120ms cubic-bezier(0.23, 1, 0.32, 1)",
            _active: { transform: "scale(0.97)" },
            _disabled: { opacity: 0.5, cursor: "not-allowed" },
          })}
        >
          Agregar
        </button>
      </div>
    </div>
  );
}
